import { useMemo } from "react";
import { useDateFnsFormattersWithLocale, type DashboardCardProps } from "@tellescope/ui";
import { isToday } from "date-fns/isToday";
import { isTomorrow } from "date-fns/isTomorrow";
import { isThisWeek } from "date-fns/isThisWeek";
import {
  useAuth,
  useTellescopeInfiniteQuery,
  useUsersMap,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { createIdMap, formatInitials } from "@tellescope/utils";
import { getDashboardStageConfig } from "../../../components/tickets/utils/stageConfig";

type TicketRecord = TellescopeModelFromName<"tickets">;

/**
 * Custom hook to fetch and transform tickets for dashboard cards
 */
export function useDashboardTicketsData(limit: number = 10) {
  const { t } = useTranslation("dashboard");
  const { format } = useDateFnsFormattersWithLocale();
  const { currentUser } = useAuth();
  const ticketsQuery = useTellescopeInfiniteQuery({
    model: "tickets",
    method: "getSome",
    request: {
      filter: { owner: currentUser.id },
      limit: limit,
      sortBy: "dueDateInMS",
      sort: "newFirst",
    },
    enabled: !!currentUser.id,
  });
  const usersById = useUsersMap();
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {},
    select: (data) => {
      const endusers = data.pages.flat();
      return {
        endusers,
        recordsById: createIdMap(endusers),
      };
    },
  });

  // Transform tickets to dashboard card format
  const ticketCards: DashboardCardProps[] = useMemo(() => {
    // Flatten the paginated data from TanStack Query
    const tickets = ticketsQuery.data?.pages.flat() || [];

    return tickets.map((ticket: TicketRecord) => {
      // Format the due date - use actual dueDateInMS if available, fallback to createdAt
      let dueDate = "No due date";
      const dateToFormat = ticket.dueDateInMS;

      // Check if we have a valid due date (number timestamp)
      const hasValidDueDate =
        dateToFormat &&
        dateToFormat !== null &&
        dateToFormat !== undefined &&
        typeof dateToFormat === "number" &&
        dateToFormat > 0;

      if (hasValidDueDate) {
        const date = new Date(dateToFormat);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
          console.warn("Invalid due date for ticket", ticket.id, ":", ticket.dueDateInMS);
          dueDate = "Invalid due date";
        } else {
          if (isToday(date)) {
            dueDate = "Due today";
          } else if (isTomorrow(date)) {
            dueDate = "Due tomorrow";
          } else if (isThisWeek(date)) {
            dueDate = "Due this week";
          } else {
            dueDate = `Due ${format(date, "MMM d")}`;
          }
        }
      }

      // Map ticket stage to dashboard status using shared utility
      const stageConfig = getDashboardStageConfig(ticket.closedAt ? "Completed" : ticket.stage);
      const status = stageConfig.status;
      const statusLabel = stageConfig.label;
      const customStatusStyles = stageConfig.customStatusStyles;

      // Get assigned users for avatars
      let assignees: Array<{ src?: string; alt?: string }> | null = null;
      if (ticket.owner) {
        const user = usersById[ticket.owner];
        assignees = [
          {
            // Only use actual user avatar if it exists, otherwise let Avatar component fall back to initials
            src: user?.avatar || undefined,
            alt: formatInitials(user || { fname: "U", lname: "ser" }),
          },
        ];
      }

      // Use the actual ticket title
      let title = ticket.title || ticket.type || t("Ticket");

      // Add enduser context as subtitle if available
      let subtitle = "";
      if (ticket.enduserId) {
        const enduser = endusersQuery.data?.recordsById[ticket.enduserId];
        if (enduser) {
          const enduserName =
            enduser.fname && enduser.lname ? `${enduser.fname} ${enduser.lname}` : enduser.email || t("Patient");
          title = t("{{type}}: {{name}}", { type: ticket.type || t("Ticket"), name: enduserName });
          subtitle = t("Patient: {{name}}", { name: enduserName });
        }
      }

      // Set priority based on ticket priority using same logic as PriorityCell
      let priority = "";
      let customPriorityStyles = "";

      switch (ticket.priority) {
        case 1:
          priority = "Low";
          customPriorityStyles = "bg-green-200 text-green-900 border-transparent";
          break;
        case 5:
          priority = "Medium";
          customPriorityStyles = "bg-yellow-200 text-yellow-900 border-transparent";
          break;
        case 10:
          priority = "High";
          customPriorityStyles = "bg-red-500 text-red-50 border-transparent";
          break;
        default:
          priority = "No status";
          customPriorityStyles = "bg-muted text-muted-foreground border-transparent";
      }

      const cardData = {
        title,
        subtitle: subtitle || undefined, // Only include subtitle if it has content
        description: ticket.message || t("No description available"),
        dueDate,
        assignees,
        statusLabel,
        status,
        priority,
        // Add timestamp for display in footer
        timestamp: ticket.createdAt,
        // Add custom status styles to override Badge defaults
        customStatusStyles,
        // Add custom priority styles
        customPriorityStyles,
        // Add ticket ID for navigation
        ticketId: ticket.id,
      };

      return cardData;
    });
  }, [ticketsQuery.data?.pages, t, format, usersById, endusersQuery.data?.recordsById]);

  return {
    ticketCards,
    isLoading: ticketsQuery.isLoading,
    error: ticketsQuery.error,
    hasLoadedInitial: !ticketsQuery.isLoading,
    // Debug info
    totalTickets: (ticketsQuery.data?.pages.flat() || []).length,
    ticketsSliceState: {
      hasLoadedInitial: !ticketsQuery.isLoading,
      isLoading: ticketsQuery.isLoading,
      error: ticketsQuery.error,
    },
  };
}
